import { useCallback, useState } from 'react';

import { createSession, QUERY_MAX_LENGTH, QUERY_MIN_LENGTH } from '@/libs/patientGpt';
import { readUtmParams } from '@/libs/utm';
import { trackInteraction } from '@/utils/analytics';

/**
 * Owns the PatientGPT question form's validation and full submit lifecycle, shared by every
 * PatientGPT entry form (the Banner and the Hero):
 *   - validate (required, 2–150 chars)
 *   - call the session client
 *   - hold pending / error state
 *   - guard double-submit
 *   - fire the `patientgpt_submission_success` data-layer event once validation passes
 *   - navigate to the returned tokenised URL
 *
 * A conversation can be started from the field (`handleSubmit`, reported as 'button' or
 * 'keyboard') or from a suggested button that carries a pre-authored question
 * (`handleQuerySubmit`, reported as 'secondary_button' with the button's label as `link_text`).
 *
 * @param {object} params
 * @param {string} params.title Component title, reported as `component_title` in analytics.
 * @param {string} params.ctaLabel Submit button label, reported as `link_text` in analytics.
 * @param {string} params.apiBaseUrl K Health API origin passed to `createSession`.
 * @param {string} params.validationErrorMessage Authored copy shown when validation fails.
 * @param {string} params.apiErrorMessage Authored copy shown when the session request fails.
 */
const usePatientGptSubmit = ({
    title,
    ctaLabel,
    apiBaseUrl,
    validationErrorMessage,
    apiErrorMessage,
}) => {
    // value must start as '' — undefined triggers React's controlled/uncontrolled warning.
    const [value, setValue] = useState('');
    const [isInvalid, setIsInvalid] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Clear any showing error unconditionally on every keystroke (both api and validation
    // copy). Never show a validation error mid-typing; the next blur or submit supplies it.
    const handleChange = useCallback((newValue) => {
        setValue(newValue);
        setIsInvalid(false);
        setErrorMessage(null);
    }, []);

    // Compute the validity verdict and surface it. The molecule forms no verdict of its own.
    const handleBlur = useCallback(() => {
        // Judge the trimmed value so whitespace-only input counts as empty.
        if (value.trim().length < QUERY_MIN_LENGTH) {
            setIsInvalid(true);
            setErrorMessage(validationErrorMessage);
        } else {
            setIsInvalid(false);
            setErrorMessage(null);
        }
    }, [value, validationErrorMessage]);

    // Starts a conversation with `query`. Guards double-submit FIRST — before the data-layer
    // event and the client call. The molecule wraps a real <form> whose CTA is type="submit", so
    // Enter in the input reaches the form handler regardless of the CTA's disabled state; the early
    // return here is the only reliable guard.
    //
    // The createSession try-scope is intentionally narrow: only the await is inside it so API
    // errors and window.open errors remain distinct code paths.
    const startSession = useCallback(
        async ({ query, actionLabel, method }) => {
            if (isPending) return;

            trackInteraction({
                eventName: 'patientgpt_submission_success',
                componentName: 'PatientGPT Widget',
                componentTitle: title,
                actionLabel,
                interactionType: 'submit',
                toolCategory: 'AI Health Guidance',
                interactionDetail: method,
            });

            setIsPending(true);

            // Forward session-stored UTM params as utm_parameters in the request body.
            // readUtmParams() is synchronous and non-throwing; it returns null when nothing is
            // stored, which createSession then omits from the body.
            const utm_parameters = readUtmParams();

            let response;
            try {
                response = await createSession({ apiBaseUrl, query, utm_parameters });
            } catch (error) {
                // ⚠️ PHI constraint: never log the query (the visitor's health question) or any
                // returned URL (carries the session token) — security-baseline.
                console.error('[usePatientGptSubmit] Session request failed:', error);
                setIsPending(false);
                setIsInvalid(true);
                setErrorMessage(apiErrorMessage);
                return;
            }

            // Open the PatientGPT experience in a new tab so the current page stays mounted.
            // noopener,noreferrer prevents the new tab from accessing window.opener and keeps
            // the referrer header off the request. Popup blockers return null instead of
            // throwing; a SecurityError in a restrictive iframe throws. Both leave newTab
            // falsy, so surface apiErrorMessage in either case so the user can retry.
            let newTab;
            try {
                newTab = window.open(response.url, '_blank', 'noopener,noreferrer');
            } catch {
                newTab = null;
            }
            setIsPending(false);
            if (!newTab) {
                setErrorMessage(apiErrorMessage);
            }
        },
        [isPending, title, apiBaseUrl, apiErrorMessage],
    );

    // Field submit: validate the visitor's question first, then start the conversation. The value
    // is trimmed before validating (whitespace-only input is empty) and the trimmed text is what
    // is sent to the backend; the field itself keeps what the visitor typed.
    const handleSubmit = useCallback(
        async (method) => {
            if (isPending) return;

            const query = value.trim();

            if (query.length < QUERY_MIN_LENGTH || query.length > QUERY_MAX_LENGTH) {
                setIsInvalid(true);
                setErrorMessage(validationErrorMessage);
                return;
            }

            await startSession({ query, actionLabel: ctaLabel, method });
        },
        [isPending, value, validationErrorMessage, ctaLabel, startSession],
    );

    // Suggested-button submit: the question is authored, not typed, so the field is untouched.
    // The authored button label (not the question) is what the data layer reports as `link_text`.
    const handleQuerySubmit = useCallback(
        async (query, label) => {
            if (!query || query.length < QUERY_MIN_LENGTH || query.length > QUERY_MAX_LENGTH) {
                return;
            }

            await startSession({ query, actionLabel: label, method: 'secondary_button' });
        },
        [startSession],
    );

    return {
        value,
        isInvalid,
        isPending,
        errorMessage,
        maxLength: QUERY_MAX_LENGTH,
        handleChange,
        handleBlur,
        handleSubmit,
        handleQuerySubmit,
    };
};

export default usePatientGptSubmit;
